import { Icon, type IconType } from '@graftcode/design-system'

export type Language = 'nodejs' | 'python' | 'csharp' | 'java' | 'ruby' | 'php'

export interface Challenge {
  id: string
  title: string
  difficulty: 'beginner' | 'intermediate' | 'advanced'
  description: string
  instructions: string | Partial<Record<Language, string>>
  supportedLanguages: Language[]
  starterCode: Partial<Record<Language, string>>
  runCommand: Partial<Record<Language, string>>
  estimatedMinutes: number
  hints: string[]
}

export function getInstructions(challenge: Challenge, language: Language): string {
  if (typeof challenge.instructions === 'string') return challenge.instructions
  return challenge.instructions[language] ?? Object.values(challenge.instructions)[0] ?? ''
}

export interface LanguageConfig {
  id: Language
  label: string
  monacoLanguage: string
  fileExtension: string
  icon: IconType
  runCommand: string
  installCommand: string
}

export const LANGUAGE_CONFIGS: Record<Language, LanguageConfig> = {
  nodejs: {
    id: 'nodejs',
    label: 'Node.js',
    monacoLanguage: 'javascript',
    fileExtension: '.js',
    icon: Icon.DSJsColor,
    runCommand: 'node main.js',
    installCommand: 'npm install',
  },
  python: {
    id: 'python',
    label: 'Python',
    monacoLanguage: 'python',
    fileExtension: '.py',
    icon: Icon.DSPythonColor,
    runCommand: 'python main.py',
    installCommand: 'pip install',
  },
  csharp: {
    id: 'csharp',
    label: 'C#',
    monacoLanguage: 'csharp',
    fileExtension: '.cs',
    icon: Icon.DSCSharpColor,
    runCommand: 'dotnet run',
    installCommand: 'dotnet add package',
  },
  java: {
    id: 'java',
    label: 'Java',
    monacoLanguage: 'java',
    fileExtension: '.java',
    icon: Icon.DSJavaColor,
    runCommand: 'javac Main.java && java Main',
    installCommand: 'mvn install',
  },
  ruby: {
    id: 'ruby',
    label: 'Ruby',
    monacoLanguage: 'ruby',
    fileExtension: '.rb',
    icon: Icon.DSRubyColor,
    runCommand: 'ruby main.rb',
    installCommand: 'gem install',
  },
  php: {
    id: 'php',
    label: 'PHP',
    monacoLanguage: 'php',
    fileExtension: '.php',
    icon: Icon.DSPackagistRegular,
    runCommand: 'php main.php',
    installCommand: 'composer require',
  },
}

export const FILE_EXTENSION_ICONS: Record<string, IconType> = {
  cs: Icon.DSCSharpColor,
  js: Icon.DSJsColor,
  mjs: Icon.DSJsColor,
  ts: Icon.DSTsColor,
  mts: Icon.DSTsColor,
  py: Icon.DSPythonColor,
  java: Icon.DSJavaColor,
  rb: Icon.DSRubyColor,
  php: Icon.DSPackagistRegular,
}

export const ALL_LANGUAGES: Language[] = ['nodejs', 'python', 'csharp', 'java', 'ruby', 'php']
